import './index.css'

import cn from 'clsx'
import React from 'react'
import { connect } from 'react-redux'

import { loadBillingProviderComponents } from '@disco/state-management/actions/billing-provider-components'
import { loadBusinessUsers } from '@disco/state-management/actions/BusinessUsersActions'
import {
  changePlansTab,
  updatePricingForm,
} from '@disco/state-management/actions/FormActions'
import type { BillingProviderComponentsState } from '@disco/state-management/reducers/billing-provider-components'
import type { PricingFormState } from '@disco/state-management/reducers/forms/pricing'
import {
  ARTIST_PLANS_TAB,
  BUSINESS_PLANS_TAB,
} from '@disco/state-management/reducers/forms/pricing'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { getDefaultPlansTab } from '@disco/state-management/selectors/forms'
import { getPlans } from '@disco/state-management/selectors/pricing'
import {
  getSubscriptionPeriod,
  isArtistPlansAvailable,
  isBusinessPlansAvailable,
} from '@disco/state-management/selectors/subscription'
import {
  getBusinessUsers,
  getConstanceSettings,
} from '@disco/state-management/selectors/user'
import type { BillingRootPath } from '@disco/utils/pricing'

import type { AccountPlan } from 'types/business2020'
import type { State } from 'types/redux'
import type { DropdownOption } from 'types/ui'
import type { User } from 'types/user'

import ChargebeeForm from 'components/chargebee-form'
import BillingPeriodSwitch from 'components/common/billing-period-switch'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import ContactUsLink from 'components/ui/contact-us-link'
import PageContainer from 'components/ui/page-container'
import Spinner from 'components/ui/spinner'
import Stack from 'components/ui/stack'
import Toggle from 'components/ui/toggle'

import type { RootState } from 'store/configureStore'

import AddOns from './add-ons'
import PricingBanner from './banner'
import PricingMobileTabs, {
  ADD_ONS_TAB_IDX,
  CHOOSE_PLAN_TAB_IDX,
  PAYMENT_DETAILS_TAB_IDX,
  TABS_COUNT,
} from './mobile-tabs'
import PricingTable from './pricing-table'

const mapStateToProps = (state: RootState) => {
  return {
    constance: getConstanceSettings(state),
    pricingForm: state.forms.pricingForm,
    plans: getPlans(state),
    paymentPeriod: getSubscriptionPeriod(state),
    activeTab: state.forms.pricingForm.plansTabs.active,
    defaultPlansTab: getDefaultPlansTab(state),
    billingProviderComponents: state.billingProviderComponents,
    businessUsers: getBusinessUsers(state),
    user: state.user,
    isPhone: isDeviceTypePhone(state),
    isBusinessPlansAvailable: isBusinessPlansAvailable(state),
    isArtistPlansAvailable: isArtistPlansAvailable(state),
  }
}

const mapDispatchToProps = {
  updatePricingForm,
  loadBillingProviderComponents,
  loadBusinessUsers,
  changePlansTab,
}
type Props = {
  deprecatedPlan: boolean
  constance: Record<string, any>
  maxFilesCurrentPlan?: number
  maxUsersCurrentPlan?: number
  onPlanSelect?: (...args: Array<any>) => any
  user: User
  toggleSidebar?: (...args: Array<any>) => any
  sidebarOpen?: boolean
  billingRef: {
    current: HTMLElement | null | undefined
  }
  pricingForm: PricingFormState
  updatePricingForm: typeof updatePricingForm
  plans: AccountPlan[]
  paymentPeriod: PaymentPeriod
  billingProviderComponents: BillingProviderComponentsState
  loadBillingProviderComponents: typeof loadBillingProviderComponents
  loadBusinessUsers: typeof loadBusinessUsers
  changePlansTab: typeof changePlansTab
  activeTab: number | null | undefined
  defaultPlansTab: number
  businessUsers: State['entities']['businessUsers']
  rootPath: BillingRootPath
  isPhone: boolean
  isBusinessPlansAvailable?: boolean
  isArtistPlansAvailable?: boolean
  isPlanChange?: boolean
  showBillingSummary?: boolean
}
export type PaymentPeriod = 'annual' | 'monthly'

/**
 * Pricing component for displaying subscription plans and pricing information
 *
 * A comprehensive pricing interface that includes plan selection, add-ons, payment details,
 * and billing configuration. Supports both desktop and mobile views with tab navigation,
 * plan comparisons, quota management, and integration with Chargebee for payment processing.
 *
 * @param props - Component props including Redux state and actions
 * @param props.deprecatedPlan - Whether user is on a deprecated plan
 * @param props.constance - Configuration settings
 * @param props.maxFilesCurrentPlan - Maximum files allowed in current plan
 * @param props.maxUsersCurrentPlan - Maximum users allowed in current plan
 * @param props.onPlanSelect - Callback for plan selection
 * @param props.user - Current user information
 * @param props.toggleSidebar - Callback to toggle sidebar
 * @param props.sidebarOpen - Whether sidebar is currently open
 * @param props.billingRef - Ref for billing form element
 * @param props.pricingForm - Pricing form state
 * @param props.plans - Available subscription plans
 * @param props.paymentPeriod - Selected payment period (annual/monthly)
 * @param props.businessUsers - Business users data
 * @param props.rootPath - Root path for billing navigation
 * @param props.isPhone - Whether device is mobile
 * @param props.isPlanChange - Whether this is a plan change flow
 * @param props.showBillingSummary - Whether to show billing summary
 * @returns JSX element containing the complete pricing interface
 */
class Pricing extends React.Component<
  Props,
  {
    planName?: string
    addOns: Record<
      string,
      DropdownOption & {
        price: number
      }
    >
    updateModal: React.ReactNode
    tabIndex: number
  }
> {
  constructor(props: Props) {
    super(props)
    this.state = {
      addOns: {},
      updateModal: null,
      tabIndex: 0,
    }
  }

  _onPeriodSelect = (paymentPeriod: PaymentPeriod) => {
    this.props.updatePricingForm({
      paymentPeriod,
    })
  }

  _renderTable(plans: any[], props: Record<string, any>) {
    return (
      <PricingTable
        maxUsersCurrentPlan={props.maxUsersCurrentPlan}
        maxFilesCurrentPlan={props.maxFilesCurrentPlan}
        deprecatedPlan={props.deprecatedPlan}
        user={this.props.user}
        plans={plans}
        onPlanSelect={this.props.onPlanSelect}
        rootPath={this.props.rootPath}
        isPlanChange={Boolean(this.props.isPlanChange)}
      />
    )
  }

  componentDidMount() {
    const {
      sidebarOpen,
      toggleSidebar,
      loadBillingProviderComponents,
      loadBusinessUsers,
      businessUsers,
      changePlansTab,
      defaultPlansTab,
      user,
    } = this.props

    if (sidebarOpen) {
      if (toggleSidebar) toggleSidebar()
    }

    loadBillingProviderComponents()

    if (!businessUsers.loading && !businessUsers.loaded) {
      loadBusinessUsers()
    }

    changePlansTab(defaultPlansTab)
    this.preSelectAddOns(user.business.private_metadata)

    if (this.props.paymentPeriod) {
      this._onPeriodSelect(this.props.paymentPeriod)
    }
  }

  componentWillUnmount() {
    const { sidebarOpen, toggleSidebar } = this.props

    if (!sidebarOpen) {
      if (toggleSidebar) toggleSidebar()
    }
  }

  checkPlanOverQuota(name?: string): boolean {
    if (!this.props.user) return false
    const {
      maxUsersCurrentPlan: userPlan,
      maxFilesCurrentPlan: filesPlan,
      user,
    } = this.props
    const usersCurrent = user.business?.stats.number_of_users
    const filesCurrent = user.business?.stats.total_track_count
    const conditionUser = userPlan ? usersCurrent > userPlan : false
    const conditionFiles = filesPlan ? filesCurrent > filesPlan : false

    if (name === 'user') {
      return conditionUser
    }

    if (name === 'file') {
      return conditionFiles
    }

    return conditionUser || conditionFiles
  }

  onSelectAddOns = (data: {
    id: string
    value: DropdownOption & {
      price: number
    }
  }) => {
    const { componentsQuantity } = this.props.pricingForm

    if (componentsQuantity[data.id] !== data.value?.value) {
      this.props.updatePricingForm({
        componentsQuantity: {
          ...componentsQuantity,
          [data.id]: data.value?.value,
        },
      })
    }
  }
  preSelectAddOns = data => {
    const { discovery_suite_quota, watermarked_playlists_quota } = data
    const { componentsQuantity } = this.props.pricingForm

    if (
      componentsQuantity.discoverySuite !== discovery_suite_quota ||
      componentsQuantity.watermarking !== watermarked_playlists_quota
    ) {
      this.props.updatePricingForm({
        componentsQuantity: {
          ...componentsQuantity,
          discoverySuite:
            discovery_suite_quota > 0 ? discovery_suite_quota : null,
          watermarking:
            watermarked_playlists_quota > 0
              ? watermarked_playlists_quota
              : null,
        },
      })
    }
  }
  onMobileTabsChange = index => {
    if (index <= TABS_COUNT) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      })
      this.setState({
        tabIndex: index,
      })
    }

    if (index > TABS_COUNT) {
      this.props.updatePricingForm({
        openBillingSummary: true,
      })
    }
  }
  isNextButtonFixed = () => {
    const {
      pricingForm: {
        componentsQuantity: { discoverySuite, watermarking },
        paymentDetailsSaved,
      },
    } = this.props
    const { tabIndex } = this.state
    let result = tabIndex === CHOOSE_PLAN_TAB_IDX

    if (!result) {
      result =
        tabIndex === ADD_ONS_TAB_IDX && Boolean(discoverySuite || watermarking)
    }

    if (!result) {
      result = tabIndex === PAYMENT_DETAILS_TAB_IDX && paymentDetailsSaved
    }

    return result
  }
  getPricingBanners = () => {
    const {
      activeTab,
      changePlansTab,
      defaultPlansTab,
      rootPath,
      isBusinessPlansAvailable,
      isArtistPlansAvailable,
    } = this.props

    if (activeTab === ARTIST_PLANS_TAB && isBusinessPlansAvailable) {
      return (
        <PricingBanner
          theme="business-plans-switch"
          title="Want to sign up for a business plan instead?"
          description={
            <>
              See our business plans{' '}
              <a
                href="https://www.disco.ac/artist-plan-comparison"
                target="_blank"
                rel="noopener noreferrer"
              >
                here
              </a>
              . Click below to sign up for a business plan instead of the Artist
              plan.
            </>
          }
          buttons={
            <Button
              appearance="primary"
              onClick={() => changePlansTab(BUSINESS_PLANS_TAB)}
              caption="Switch to business plans"
            />
          }
        />
      )
    }

    let artistCtaApply: React.ReactNode = null

    if (!this.props.isPlanChange) {
      if (defaultPlansTab === ARTIST_PLANS_TAB) {
        artistCtaApply = (
          <Button
            appearance="primary"
            onClick={() => changePlansTab(ARTIST_PLANS_TAB)}
            caption="Apply"
          />
        )
      } else {
        artistCtaApply = (
          <Button
            appearance="primary"
            caption="Apply"
            link={`${rootPath}/artist-signup/apply`}
          />
        )
      }
    }

    const artistCta = activeTab !== ARTIST_PLANS_TAB &&
      isArtistPlansAvailable && (
        <PricingBanner
          theme="artist"
          title="Are you an artist?"
          description="We have a dedicated plan for artists, songwriters, producers, engineers and bands."
          buttons={
            <ButtonContainer>
              <Button
                appearance={artistCtaApply ? 'subtle' : 'primary'}
                caption="Learn more"
                link="https://www.disco.ac/artist-plan-comparison?utm_source=pricing_page"
                linkTarget="_blank"
              />
              {artistCtaApply}
            </ButtonContainer>
          }
        />
      )
    const enterpriseCta = isBusinessPlansAvailable && (
      <PricingBanner
        theme="enterprise"
        title="Enterprise"
        description="Everything on Pro, plus API access, SSO, a dedicated account manager and more."
        buttons={
          <ContactUsLink intercomMsg="I’m interested in the Enterprise plan">
            <Button appearance="primary" caption="Find out more" />
          </ContactUsLink>
        }
      />
    )
    return (
      <Stack>
        {enterpriseCta}
        {artistCta}
      </Stack>
    )
  }

  render() {
    const {
      constance,
      maxFilesCurrentPlan,
      maxUsersCurrentPlan,
      user,
      deprecatedPlan,
      pricingForm,
      billingProviderComponents,
      isPhone,
      isPlanChange,
      showBillingSummary,
    } = this.props
    const number_of_users = user?.business?.stats?.number_of_users || 0
    const total_track_count = user?.business?.stats?.total_track_count || 0

    if (!billingProviderComponents.loaded) {
      return <Spinner />
    }

    // Set or remove the localStorage flag for QA purposes
    // localStorage.setItem('show_2024_design', 'true')
    // localStorage.removeItem('show_2024_design')
    //TODO: remove Pricing_ToggleContainer once we're live
    const show2024Design =
      constance.PLAN_DOWNGRADES_ROLLOUT ||
      Boolean(localStorage.getItem('show_2024_design'))
    const periodToggle = !show2024Design ? (
      <div className="Pricing_ToggleContainer">
        <span className="Pricing_ToggleText">Monthly billing</span>
        <Toggle
          checked={this.props.pricingForm.paymentPeriod === 'annual'}
          onToggle={() =>
            this._onPeriodSelect(
              this.props.pricingForm.paymentPeriod === 'annual'
                ? 'monthly'
                : 'annual',
            )
          }
        />
        <span className="Pricing_ToggleText Pricing_ToggleText--Annual">
          Annual Billing
        </span>
      </div>
    ) : (
      <BillingPeriodSwitch />
    )
    return (
      <div className="Pricing">
        {isPhone && (
          <PricingMobileTabs
            onChange={this.onMobileTabsChange}
            tabIndex={this.state.tabIndex}
          />
        )}
        <PageContainer.Section
          title={
            !isPhone && <div className="Pricing_Title">Choose base plan</div>
          }
          action={!isPhone && periodToggle}
        >
          <Stack space="large">
            {this.state.tabIndex === 0 || !isPhone ? (
              <>
                {isPhone && (
                  <div className="Pricing_Title">Choose base plan</div>
                )}
                {isPhone && periodToggle}
                {/* TODO: refactoring:
              maxUsersCurrentPlan is the code from the old Pricing component.
              Previously, maxUsersCurrentPlan was used in the plan upgrade/downgrade logic.
              Currently Pricing component does not include the upgrade/downgrade plan flow
              asana task: https://app.asana.com/0/0/1201426247015104/f
              */}
                {Boolean(maxUsersCurrentPlan || total_track_count) && (
                  <div className="Pricing_UsedQuotaAlert">
                    <Alert status="warning">
                      You currently have {maxUsersCurrentPlan} users and{' '}
                      {total_track_count} tracks on your DISCO.
                    </Alert>
                  </div>
                )}

                {this._renderTable(this.props.plans, {
                  maxUsersCurrentPlan,
                  maxFilesCurrentPlan,
                  deprecatedPlan,
                })}
                {this.getPricingBanners()}
              </>
            ) : null}

            {!isPhone && <h2 className="Pricing_Title">Add ons</h2>}
          </Stack>

          {this.state.tabIndex === 1 || !isPhone ? (
            <AddOns
              billingProviderComponents={this.props.billingProviderComponents}
              pricingForm={this.props.pricingForm}
              values={this.props.pricingForm.componentsQuantity}
              onSelectAddOns={this.onSelectAddOns}
            />
          ) : null}

          {!isPhone && !isPlanChange && (
            <h2 className="Pricing_Title">Payment details</h2>
          )}

          {!isPlanChange && (
            <ChargebeeForm
              usersQuantity={pricingForm.componentsQuantity.users ?? 0}
              tracksQuantity={pricingForm.componentsQuantity.tracks ?? 0}
              discoverySuiteQuantity={
                pricingForm.componentsQuantity.discoverySuite ?? 0
              }
              watermarkingQuantity={
                typeof pricingForm.componentsQuantity.watermarking === 'number'
                  ? pricingForm.componentsQuantity.watermarking
                  : 0
              }
              setUpdateModal={updateModal => {
                this.setState({
                  updateModal,
                })
              }}
              onRequestCloseUpdateModal={() => {
                this.setState({
                  updateModal: null,
                })
              }}
              onRequestClose={() => {}}
              period={this.props.paymentPeriod}
              isChosenPlan={true}
              defaultCountFiles={0}
              defaultCountUser={0}
              billingRef={this.props.billingRef}
              rootPath={this.props.rootPath}
              showAllMobileContent={this.state.tabIndex === 2}
              showBillingSummary={showBillingSummary}
            />
          )}
          {isPhone &&
            (this.state.tabIndex < TABS_COUNT || this.isNextButtonFixed()) && (
              <div
                className={cn({
                  'Pricing_NextButtonContainer--Fixed':
                    this.isNextButtonFixed(),
                })}
              >
                <Button
                  appearance="primary"
                  onClick={() => {
                    this.onMobileTabsChange(this.state.tabIndex + 1)
                  }}
                  caption="Next"
                  fullWidth
                  textCenter
                />
              </div>
            )}

          {isPhone && (
            <div
              className={cn('ChargebeeForm_MobileSpacer', {
                'ChargebeeForm_MobileSpacer--WithNextButton':
                  this.isNextButtonFixed(),
              })}
            />
          )}
        </PageContainer.Section>
        {this.state.updateModal}
      </div>
    )
  }
}

// @ts-expect-error type connector
export default connect(mapStateToProps, mapDispatchToProps)(Pricing) as any
